import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TextInput,
  TouchableOpacity,
  FlatList,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/colors';
import { tickerDetectionService } from '@/services/stock/tickerDetectionService';

interface TickerTagPickerProps {
  visible: boolean;
  onClose: () => void;
  onSelect: (ticker: string) => void;
  selectedTickers: string[];
  recentTickers?: string[]; // Recent tickers from group
}

interface TickerSuggestion {
  ticker: string;
  name: string;
  source: 'recent' | 'common' | 'search';
}

/**
 * Ticker Tag Picker Component
 * Allows users to manually tag stock tickers while composing messages
 */
export const TickerTagPicker: React.FC<TickerTagPickerProps> = ({
  visible,
  onClose,
  onSelect,
  selectedTickers,
  recentTickers = [],
}) => {
  const [search, setSearch] = useState('');
  const [suggestions, setSuggestions] = useState<TickerSuggestion[]>([]);
  const [loading, setLoading] = useState(false);

  // Common popular tickers
  const commonTickers: TickerSuggestion[] = [
    { ticker: 'TSLA', name: 'Tesla, Inc.', source: 'common' },
    { ticker: 'AAPL', name: 'Apple Inc.', source: 'common' },
    { ticker: 'NVDA', name: 'NVIDIA Corporation', source: 'common' },
    { ticker: 'MSFT', name: 'Microsoft Corporation', source: 'common' },
    { ticker: 'GOOGL', name: 'Alphabet Inc.', source: 'common' },
    { ticker: 'AMZN', name: 'Amazon.com, Inc.', source: 'common' },
    { ticker: 'META', name: 'Meta Platforms, Inc.', source: 'common' },
    { ticker: 'AMD', name: 'Advanced Micro Devices, Inc.', source: 'common' },
  ];

  // Initialize suggestions with recent and common tickers
  useEffect(() => {
    if (visible && !search) {
      const recentSuggestions: TickerSuggestion[] = recentTickers.map(ticker => ({
        ticker,
        name: '',
        source: 'recent' as const,
      }));

      setSuggestions([...recentSuggestions, ...commonTickers]);
    }
  }, [visible, search]);

  // Search for tickers as user types
  useEffect(() => {
    if (search.trim().length === 0) {
      return;
    }

    const searchTickers = async () => {
      setLoading(true);

      try {
        const result = await tickerDetectionService.detectTicker(search);

        if (result.ticker) {
          const searchResults: TickerSuggestion[] = [
            {
              ticker: result.ticker,
              name: result.companyName,
              source: 'search' as const,
            },
            ...result.suggestions.map(s => ({
              ticker: s.symbol,
              name: s.name,
              source: 'search' as const,
            })),
          ];

          setSuggestions(searchResults);
        } else if (result.suggestions.length > 0) {
          const searchResults: TickerSuggestion[] = result.suggestions.map(s => ({
            ticker: s.symbol,
            name: s.name,
            source: 'search' as const,
          }));

          setSuggestions(searchResults);
        } else {
          setSuggestions([]);
        }
      } catch (error) {
        console.error('Error searching tickers:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(searchTickers, 300);
    return () => clearTimeout(debounce);
  }, [search]);

  const handleSelect = (ticker: string) => {
    if (!selectedTickers.includes(ticker)) {
      onSelect(ticker);
    }
    setSearch('');
  };

  const handleClose = () => {
    setSearch('');
    onClose();
  };

  const renderTickerItem = ({ item }: { item: TickerSuggestion }) => {
    const isSelected = selectedTickers.includes(item.ticker);

    return (
      <TouchableOpacity
        style={[styles.tickerItem, isSelected && styles.tickerItemSelected]}
        onPress={() => handleSelect(item.ticker)}
        disabled={isSelected}
        activeOpacity={0.7}
      >
        <View style={styles.tickerInfo}>
          <View style={styles.tickerHeader}>
            <Text style={[styles.tickerSymbol, isSelected && styles.tickerSymbolSelected]}>
              ${item.ticker}
            </Text>
            {item.source === 'recent' && (
              <View style={styles.recentBadge}>
                <Text style={styles.recentBadgeText}>Recent</Text>
              </View>
            )}
          </View>
          {item.name && (
            <Text
              style={[styles.tickerName, isSelected && styles.tickerNameSelected]}
              numberOfLines={1}
            >
              {item.name}
            </Text>
          )}
        </View>
        {isSelected && <Ionicons name="checkmark-circle" size={20} color={Colors.accent.primary} />}
      </TouchableOpacity>
    );
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={handleClose}
    >
      <View style={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>Tag Stocks</Text>
          <TouchableOpacity onPress={handleClose} style={styles.closeButton}>
            <Ionicons name="close" size={24} color={Colors.text.primary} />
          </TouchableOpacity>
        </View>

        {/* Search Input */}
        <View style={styles.searchContainer}>
          <Ionicons
            name="search"
            size={20}
            color={Colors.text.tertiary}
            style={styles.searchIcon}
          />
          <TextInput
            style={styles.searchInput}
            placeholder="Search ticker or company name..."
            placeholderTextColor={Colors.text.tertiary}
            value={search}
            onChangeText={setSearch}
            autoCapitalize="characters"
            autoCorrect={false}
          />
          {loading && <ActivityIndicator size="small" color={Colors.accent.primary} />}
        </View>

        {/* Selected Tickers */}
        {selectedTickers.length > 0 && (
          <View style={styles.selectedContainer}>
            <Text style={styles.selectedTitle}>Selected ({selectedTickers.length})</Text>
            <View style={styles.selectedTickers}>
              {selectedTickers.map(ticker => (
                <View key={ticker} style={styles.selectedChip}>
                  <Text style={styles.selectedChipText}>${ticker}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Suggestions List */}
        <FlatList
          data={suggestions}
          renderItem={renderTickerItem}
          keyExtractor={(item, index) => `${item.ticker}-${index}`}
          contentContainerStyle={styles.listContainer}
          ListHeaderComponent={
            !search && (
              <Text style={styles.sectionTitle}>
                {recentTickers.length > 0 ? 'Recent & Popular' : 'Popular Stocks'}
              </Text>
            )
          }
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <Ionicons name="search-outline" size={48} color={Colors.text.tertiary} />
              <Text style={styles.emptyText}>
                {loading ? 'Searching...' : search ? 'No tickers found' : 'Start typing to search'}
              </Text>
            </View>
          }
        />

        {/* Footer Hint */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>💡 Tip: Tag stocks to help categorize your message</Text>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background.primary,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
    color: Colors.text.primary,
  },
  closeButton: {
    padding: 4,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: Colors.background.secondary,
    marginHorizontal: 20,
    marginVertical: 16,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 15,
    color: Colors.text.primary,
  },
  selectedContainer: {
    paddingHorizontal: 20,
    paddingBottom: 16,
  },
  selectedTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: Colors.text.secondary,
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  selectedTickers: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  selectedChip: {
    backgroundColor: Colors.accent.primary,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  selectedChipText: {
    fontSize: 13,
    fontWeight: '600',
    color: Colors.text.primary,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: Colors.text.secondary,
    marginBottom: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  listContainer: {
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  tickerItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: Colors.background.secondary,
    padding: 16,
    borderRadius: 12,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
  },
  tickerItemSelected: {
    borderColor: Colors.accent.primary,
    backgroundColor: `${Colors.accent.primary}10`,
  },
  tickerInfo: {
    flex: 1,
  },
  tickerHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  tickerSymbol: {
    fontSize: 16,
    fontWeight: '700',
    color: Colors.text.primary,
    marginRight: 8,
  },
  tickerSymbolSelected: {
    color: Colors.accent.primary,
  },
  tickerName: {
    fontSize: 13,
    color: Colors.text.secondary,
  },
  tickerNameSelected: {
    color: Colors.text.primary,
  },
  recentBadge: {
    backgroundColor: `${Colors.ai.primary}20`,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  recentBadgeText: {
    fontSize: 10,
    fontWeight: '600',
    color: Colors.ai.primary,
  },
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyText: {
    fontSize: 15,
    color: Colors.text.tertiary,
    marginTop: 12,
  },
  footer: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: Colors.border.subtle,
  },
  footerText: {
    fontSize: 12,
    color: Colors.text.tertiary,
    textAlign: 'center',
  },
});
